<script lang="ts" setup>
import type { FormState } from "~~/models/forms";
import type { ProductListItem, ProductOverpackItem, ProductPackagingInfo } from "~~/models/products";
import type { BasketProductResponseItem } from "~~/server/api/ecom/baskets/add.post";
import type { TemplateProductResponseItem } from "~~/server/api/ecom/templates/[basketid]/lines.post";
import type { CatalogPageRecord } from "~~/server/graphql/datocms/generated";
import type { TrackProductInfo } from "~/composables/useGtmTracking";
import { PlanktonPredefinedRight } from "~~/server/plankton";
import { determineOverpackCode, determineOverpackValue } from "~~/server/utils/products";

const { cmsData, product, overpack, addToBasket, replacements } = defineProps<{
  cmsData: CatalogPageRecord;
  product: ProductListItem;
  overpack?: { selection: ProductOverpackItem | null; packaging: ProductPackagingInfo | null } | null;
  overpackPopper: { placement: "top" | "bottom" };
  addToBasket: { show: { button: boolean; label: boolean }; class: string; containerClass: string; listName: "Results" | "Details" };
  addToTemplate: { show: { button: boolean } };
  replacements: { show: boolean };
}>();
const emit = defineEmits<{
  updatedOverpack: [overpackId: number];
}>();

const { trackAddToCart } = useGtmTracking();
const { t } = useI18n();
const localePath = useLocalePath();

const { user } = useUserSession();
const { execute: refreshBasket } = await useBasket(user.value, { initialize: false, setStatus: false });

const formState = reactive({ quantity: product.minimumOrderQuantity, overpackId: product.defaultOverpack?.value ?? 0 });
const addToBasketStatus = ref<FormState>("idle");

// packaging
const formattedPackaging = computed(() => {
  if (!overpack?.packaging) {
    return "";
  }

  if (overpack.packaging.type === "normal") {
    return t("product.packaging.normal", { Units: overpack.packaging.units, Container: overpack.packaging.container });
  }
  else if (overpack.packaging.type === "default") {
    return t("product.packaging.default", { Units: overpack.packaging.units, Container: overpack.packaging.container });
  }

  return "";
});

// replacements
const { data: replacementsData, execute: refreshReplacements } = useFetch("/api/ecom/products/replacements", {
  query: { productIdList: [product?.id] },
  immediate: false,
});

const replacement = computed(() => replacementsData.value?.at(0) ?? null);

const replacementsText = computed(() => {
  return cmsData.replacementsText.replaceAll("{product}", product.name);
});

if (replacements.show) {
  await refreshReplacements();
}

async function onAddToBasket() {
  addToBasketStatus.value = "pending";

  try {
    const data = await $fetch(`/api/ecom/baskets/add`, {
      method: "POST",
      body: [{
        productId: product.id,
        productNumber: product.number,
        productName: product.name,
        quantity: formState.quantity,
        overpackId: determineOverpackValue(formState.overpackId, product),
        overpackCode: determineOverpackCode(formState.overpackId, product),
        isVariableWeight: product.isVariableWeight,
        attributionList: addToBasket.listName,
      }],
    });

    const addedProduct = data.addedProducts.at(0) as BasketProductResponseItem;
    const replacedProduct = data.replacedProducts.at(0) as BasketProductResponseItem;
    const ignoredProduct = data.ignoredProducts.at(0) as BasketProductResponseItem;

    if (addedProduct) {
      useSuccessToast(
        t("common.title.success"),
        cmsData.textAddToBasketConfirmationAdded.replaceAll("{name}", addedProduct.productName),
        { actions: [
          {
            label: cmsData.actionGoToBasket,
            variant: "solid",
            color: "primary",
            size: "md",
            onClick: () => { navigateTo(localePath("/checkout")); },
          },
        ] },
      );

      trackAddToCart([{
        dataLayer: product.dataLayer,
        quantity: addedProduct.quantity,
        unitPriceGross: addedProduct.unitPriceGross ?? 0,
        discountAmount: addedProduct.discountPrice ?? 0,
        currencyCode: addedProduct.currencyCode,
        listName: addToBasket.listName,
      } satisfies TrackProductInfo]);
    }

    if (replacedProduct && replacedProduct.dataLayer) {
      useWarningToast(
        t("common.title.warning"),
        cmsData.textAddToBasketConfirmationReplaced.replaceAll("{name}", replacedProduct.productName),
        {
          actions: [{
            label: cmsData.actionGoToBasket,
            variant: "solid",
            color: "primary",
            size: "md",
            onClick: () => { navigateTo(localePath("/checkout")); },
          }],
        },
      );

      trackAddToCart([{
        dataLayer: replacedProduct.dataLayer,
        quantity: replacedProduct.quantity,
        unitPriceGross: replacedProduct.unitPriceGross ?? 0,
        discountAmount: replacedProduct.discountPrice ?? 0,
        currencyCode: replacedProduct.currencyCode,
        listName: addToBasket.listName,
      } satisfies TrackProductInfo]);
    }

    if (ignoredProduct) {
      useWarningToast(t("common.title.warning"), cmsData.textAddToBasketWarning.replaceAll("{name}", ignoredProduct.productName));
    }

    await refreshBasket();
  }
  catch (error) {
    console.error("Error adding product to basket:", error);
    useFailToast(t("common.title.error"), createFetchErrorMessage(error));
  }
  finally {
    addToBasketStatus.value = "idle";
  }
}

async function onTemplateSelected(templateId: number) {
  try {
    const data = await $fetch(`/api/ecom/templates/${templateId}/lines`, {
      method: "POST",
      body: [{
        productId: product.id,
        productName: product.name,
        quantity: 0, // quantity is ignored when adding to template
        overpackId: determineOverpackValue(formState.overpackId, product),
        overpackCode: determineOverpackCode(formState.overpackId, product),
      }],
    });

    const addedProduct = data.addedProducts.at(0) as TemplateProductResponseItem;
    const ignoredProduct = data.ignoredProducts.at(0) as TemplateProductResponseItem;

    if (addedProduct) {
      useSuccessToast(
        t("common.title.success"),
        cmsData.detailAddToTemplateTextConfirmation.replaceAll("{name}", addedProduct.productName),
      );
    }

    if (ignoredProduct) {
      useWarningToast(
        t("common.title.warning"),
        cmsData.detailAddToTemplateTextWarning.replaceAll("{name}", ignoredProduct.productName),
      );
    }
  }
  catch (error) {
    console.error("Error adding product to template:", error);
    useFailToast(t("common.title.error"), createFetchErrorMessage(error));
  }
}

watch(() => formState.overpackId, async (newValue) => {
  emit("updatedOverpack", newValue);
});
</script>

<template>
  <UForm :state="formState" class="gap-xs flex flex-col" @submit.prevent="onAddToBasket">
    <div class="gap-2xs flex flex-col">
      <div class="gap-sm flex flex-col sm:flex-row">
        <div class="w-full sm:max-w-[50%]">
          <ProductOrderQuantity
            v-model="formState.quantity"
            :minimum-order-quantity="product.minimumOrderQuantity"
            :incremental-order-quantity="product.incrementalOrderQuantity"
          />
        </div>

        <div v-if="product.overpacks" class="grow">
          <USelectMenu
            v-model="formState.overpackId" :search-input="false" :items="product.overpacks"
            selected-icon="" label-key="singleName" value-key="value"
            size="lg" data-1p-ignore :content="{ side: overpackPopper.placement }"
          >
            <template #default>
              <span v-if="formState.overpackId" class="truncate xs:max-w-[75px]">{{ overpack?.selection?.singleName }}</span>
            </template>
          </USelectMenu>
        </div>
      </div>

      <div class="text-end">
        <span class="block h-[28px] truncate text-xs font-medium" :title="formattedPackaging">{{ formattedPackaging }}</span>
      </div>
    </div>

    <div v-if="replacements.show && replacement">
      <ProductCardReplacement
        :title="cmsData.replacementsTitle"
        :description="replacementsText ?? ''"
        :product="replacement"
      />
    </div>

    <div class="flex" :class="addToBasket.containerClass">
      <div class="gap-sm flex flex-col items-center sm:flex-row lg:flex-col xl:flex-row" :class="addToBasket.class">
        <RequireRight :right="PlanktonPredefinedRight.CreateNormalBasket">
          <UButton
            type="submit" variant="solid" color="primary" size="md" :class="addToBasket.class"
            icon="i-ph-shopping-cart-simple" trailing :loading="addToBasketStatus === 'pending'" data-test="add-to-basket-button"
          >
            <UIcon v-if="!addToBasket.show.label" name="i-ph-plus" class="text-xl text-white" />
            <span v-if="addToBasket.show.label">{{ cmsData.actionAddToBasket }}</span>
          </UButton>
        </RequireRight>

        <RequireRight :right="PlanktonPredefinedRight.CreateTemplateBasket">
          <LazyTemplateModalAddTrigger
            v-if="addToTemplate.show.button"
            :translations="{
              title: cmsData.detailAddToTemplateTitle,
              textSelectTemplate: cmsData.detailAddToTemplateTextSelectTemplate,
              textAddTemplate: cmsData.detailAddToTemplateTextAddTemplate,
              placeholderAddTemplate: cmsData.detailAddToTemplatePlaceholderAddTemplate,
              actionConfirmation: cmsData.detailAddToTemplateActionConfirmation,
              actionCancel: cmsData.detailAddToTemplateActionCancel,
            }"
            :trigger="{ variant: 'link', color: 'primary', label: cmsData.detailActionAddToTemplate }"
            @selected="onTemplateSelected"
          />
        </RequireRight>
      </div>
    </div>
  </UForm>
</template>

<style scoped>

</style>
